/**
 * Cart Grouping - Moon Moon Agency
 *
 * Suppression en cascade des lignes panier liees par la convention `_mm_bundle_*` :
 * une ligne "parent" porte le produit principal, une ou plusieurs lignes "enfant"
 * portent les add-ons factures a part (configurateur, personnalisation produit).
 * Retirer la ligne parent doit aussi retirer ses add-ons.
 *
 * L'affichage groupe (titre nettoye, sous-lignes "+ Monogramme  + 35 €" avec
 * leurs propres proprietes) est rendu cote Liquid par mm-cart-item.liquid /
 * mm-cart-item-addon.liquid — les lignes enfant ne sont jamais rendues en
 * standalone (voir MM-Cart.liquid). Ce module ne gere donc plus que
 * l'interaction de suppression, qui necessite un listener JS.
 *
 * Le Liquid expose la liste des variant IDs enfant sur la ligne parent via
 * `data-bundle-children="id1,id2"` (voir mm-cart-item.liquid).
 */

const CartGroupingManager = {
	config: {
		selectors: {
			line: '[data-line-index="{{index}}"]',
		},
	},

	state: { bound: false },

	init() {
		if (this.state.bound) return;
		this.state.bound = true;

		this._bindCascadeRemoval();
	},

	/**
	 * Suppression en cascade : retirer la ligne parent retire aussi ses add-ons.
	 * Sans cela, l'add-on facture resterait seul au panier alors que le produit
	 * qu'il personnalise a disparu.
	 *
	 * Branche sur `cart:before-item-remove` (cancelable), emis par mm_cart.js
	 * pour le bouton "Supprimer" ET pour le "-" qui ramene la quantite a 0 :
	 * intercepter le seul clic sur "Supprimer" laissait passer le "-", et
	 * l'add-on restait au panier, invisible mais compte dans le total.
	 */
	_bindCascadeRemoval() {
		document.addEventListener('cart:before-item-remove', (event) => {
			const lineIndex = event.detail?.lineIndex;
			if (!lineIndex) return;

			const line = document.querySelector(this.config.selectors.line.replace('{{index}}', lineIndex));
			const childrenIds = this._childrenIds(line);
			if (!childrenIds.length) return;

			event.preventDefault();
			this._removeGroup(line, childrenIds);
		});
	},

	async _removeGroup(parentLine, childrenIds) {
		const updates = {};

		const parentVariantId = parentLine.dataset.cartItemId;
		if (parentVariantId) updates[parentVariantId] = 0;
		childrenIds.forEach((variantId) => {
			updates[variantId] = 0;
		});

		const sectionId = this._cartSectionId();
		const body = { updates };
		if (sectionId) body.sections = sectionId;

		// shopify:cart:lines-update au DEBUT de la suppression (spec) : ligne parent + add-ons,
		// identifies par leur cle de ligne (data-line-key, data-bundle-children-keys).
		const lineKeys = [parentLine.dataset.lineKey, ...this._splitList(parentLine.dataset.bundleChildrenKeys)].filter(Boolean);
		const standardEvent = window.mmStandardEvents?.linesUpdate(document.querySelector('[data-drawer-cart]'), {
			action: 'remove',
			context: 'cart',
			lines: lineKeys.map((key) => ({ id: key, quantity: 0 })),
		});

		try {
			// CartAPI.request rafraichit le drawer et emet les evenements panier.
			const data = await window.CartAPI.request('POST', 'cart/update.js', body);
			standardEvent?.resolve(data);
			window.emit('cart:group-removed', { response: data, updates });
		} catch (error) {
			standardEvent?.fail(error);
			window.log('error', '🛒 [CART_GROUPING]', 'Group removal failed', { error: error.message });
		} finally {
			// Filet : la promise de l'event standard doit toujours etre reglee
			if (standardEvent && !standardEvent.settled) standardEvent.resolve();
		}
	},

	_childrenIds(line) {
		return this._splitList(line && line.dataset.bundleChildren);
	},

	_splitList(raw) {
		if (!raw) return [];
		return raw.split(',').filter(Boolean);
	},

	_cartSectionId() {
		const drawer = document.querySelector('[data-drawer-cart]');
		const section = drawer && drawer.closest('[id^="shopify-section-"]');
		return section ? section.id.replace('shopify-section-', '') : '';
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => CartGroupingManager.init());
} else {
	CartGroupingManager.init();
}

window.CartGroupingManager = CartGroupingManager;
